import React, { useState, useEffect } from 'react';

const COOKIE_CONSENT_KEY = 'kk_cookie_consent';

// Bump this whenever your cookie policy changes materially —
// it invalidates every stored consent and re-prompts everyone.
const POLICY_VERSION = 1;

// How long a saved choice stays valid before we ask again.
const CONSENT_TTL_MS = 1000 * 60 * 60 * 24 * 30 * 6; // ~6 months

function readCookieConsent() {
  try {
    const raw = window.localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    const { value, timestamp, version } = parsed;

    if (!value || !timestamp || version !== POLICY_VERSION) return null;
    if (Date.now() - timestamp > CONSENT_TTL_MS) return null;

    return value; // 'accepted' | 'declined'
  } catch {
    return null;
  }
}

function writeCookieConsent(value) {
  try {
    window.localStorage.setItem(
      COOKIE_CONSENT_KEY,
      JSON.stringify({ value, timestamp: Date.now(), version: POLICY_VERSION })
    );
  } catch {
    /* storage unavailable */
  }
}

// ── Palette (KaaKazini brand: yellow / white / black) ───────────────────────
const colors = {
  bg: '#141414',
  border: '#2c2c2c',
  iconBg: '#1f1f1f',
  accent: '#f5c518',
  accentHover: '#ffd541',
  title: '#ffffff',
  body: '#c9c9c9',
  declineBorder: '#4a4a4a',
  declineBorderHover: '#ffffff',
  declineBgHover: '#1f1f1f',
  declineText: '#ffffff',
};

// onVisibleChange lets the parent (App.js) know when the banner is on screen,
// so it can nudge the AI chat bubble up out of the way.
export default function CookieConsentBanner({ onVisibleChange }) {
  const [choice, setChoice] = useState(() => readCookieConsent());
  const [visible, setVisible] = useState(() => {
    const isVisible = !readCookieConsent();
    if (onVisibleChange) onVisibleChange(isVisible);
    return isVisible;
  });

  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= 640
  );
  const [declineHover, setDeclineHover] = useState(false);
  const [acceptHover, setAcceptHover] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    // trigger the rise-up animation on first paint
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const decide = (value) => {
    writeCookieConsent(value);
    setChoice(value);
    setVisible(false);
    if (onVisibleChange) onVisibleChange(false);
  };

  if (!visible || choice) return null;

  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const styles = {
    banner: {
      position: 'fixed',
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 1000,
      background: colors.bg,
      borderTop: `1px solid ${colors.border}`,
      boxShadow: '0 -8px 24px rgba(0, 0, 0, 0.35)',
      padding: isMobile ? '16px 18px' : '18px 24px',
      transform: prefersReducedMotion
        ? 'translateY(0)'
        : mounted
        ? 'translateY(0)'
        : 'translateY(100%)',
      transition: prefersReducedMotion ? 'none' : 'transform 0.35s ease-out',
      boxSizing: 'border-box',
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    inner: {
      maxWidth: 1080,
      margin: '0 auto',
      display: 'flex',
      alignItems: isMobile ? 'flex-start' : 'center',
      flexWrap: isMobile ? 'wrap' : 'nowrap',
      gap: isMobile ? 12 : 20,
    },
    icon: {
      flexShrink: 0,
      width: 40,
      height: 40,
      borderRadius: 8,
      background: colors.iconBg,
      border: `1px solid ${colors.border}`,
      color: colors.accent,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '1.1rem',
    },
    text: {
      flex: isMobile ? '1 1 100%' : '1 1 auto',
      minWidth: 0,
      order: isMobile ? 1 : 0,
    },
    title: {
      margin: '0 0 4px',
      fontSize: '0.95rem',
      fontWeight: 600,
      color: colors.title,
    },
    body: {
      margin: 0,
      fontSize: '0.85rem',
      lineHeight: 1.5,
      color: colors.body,
      maxWidth: isMobile ? 'none' : '60ch',
    },
    actions: {
      flexShrink: 0,
      flex: isMobile ? '1 1 100%' : '0 0 auto',
      order: isMobile ? 2 : 0,
      display: 'flex',
      gap: 10,
    },
    btnBase: {
      padding: '9px 20px',
      borderRadius: 6,
      fontSize: '0.85rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'background 0.15s ease, border-color 0.15s ease',
      flex: isMobile ? 1 : '0 0 auto',
    },
    decline: {
      background: declineHover ? colors.declineBgHover : 'transparent',
      border: `1px solid ${declineHover ? colors.declineBorderHover : colors.declineBorder}`,
      color: colors.declineText,
    },
    accept: {
      background: acceptHover ? colors.accentHover : colors.accent,
      border: `1px solid ${acceptHover ? colors.accentHover : colors.accent}`,
      color: '#141414',
    },
  };

  return (
    <div style={styles.banner} role="dialog" aria-live="polite" aria-label="Cookie consent">
      <div style={styles.inner}>
        <div style={styles.icon} aria-hidden="true">
          <i className="fas fa-cookie-bite" />
        </div>
        <div style={styles.text}>
          <p style={styles.title}>We use cookies</p>
          <p style={styles.body}>
            KaaKazini uses cookies to keep you signed in, remember your preferences and understand how the
            site is used. You can accept all cookies or decline non-essential ones at any time.
          </p>
        </div>
        <div style={styles.actions}>
          <button
            style={{ ...styles.btnBase, ...styles.decline }}
            onMouseEnter={() => setDeclineHover(true)}
            onMouseLeave={() => setDeclineHover(false)}
            onFocus={() => setDeclineHover(true)}
            onBlur={() => setDeclineHover(false)}
            onClick={() => decide('declined')}
          >
            Decline
          </button>
          <button
            style={{ ...styles.btnBase, ...styles.accept }}
            onMouseEnter={() => setAcceptHover(true)}
            onMouseLeave={() => setAcceptHover(false)}
            onFocus={() => setAcceptHover(true)}
            onBlur={() => setAcceptHover(false)}
            onClick={() => decide('accepted')}
          >
            Accept All
          </button>
        </div>
      </div>
    </div>
  );
}